import type { Filter, FilterDataRange } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { getMaxPrice } from '@/app/components/search/components/Filters/verticalFilters/Fields/getPriceField/priceUtils.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';

type CreatePriceValue = {
  searchParams: SearchParams;
  from: number;
  to: number;
};
const createPriceValue = ({ searchParams, from, to }: CreatePriceValue) => {
  let min =
    (searchParams[URLParameters.PRICE] && parseInt(searchParams[URLParameters.PRICE].split(',')[0], 10)) || from;
  let max = (searchParams[URLParameters.PRICE] && parseInt(searchParams[URLParameters.PRICE].split(',')[1], 10)) || to;
  if (max > to) {
    max = to;
  }
  if (min < from || min > to) {
    min = from;
  }
  return { min, max };
};

type CreateDiscountFilters = {
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  localization: Localization;
  dynamicFiltersResponse: DynamicFiltersResponse | undefined;
  translationDictionary: TranslationDictionary;
};
export const createPriceFilters = ({
  searchParams,
  searchInfoData,
  localization,
  dynamicFiltersResponse,
  translationDictionary,
}: CreateDiscountFilters): Filter[] => {
  const { searchMode } = searchInfoData;
  const { lang, currency } = localization;
  const maxPrice = getMaxPrice(lang, currency);
  const dynamicPrice = dynamicFiltersResponse?.price;
  const priceValue = createPriceValue({
    searchParams,
    from: dynamicPrice?.from ? dynamicPrice.from : 0,
    to: dynamicPrice?.to ? dynamicPrice.to : maxPrice,
  });
  const dynamicValues = dynamicPrice
    ? {
        dynamicValues: {
          from: dynamicPrice.from,
          to: dynamicPrice.to,
          interval: dynamicPrice.interval,
          diagram: dynamicPrice.diagram,
        },
      }
    : {};

  const priceRangeLabel =
    searchMode === SearchModes.DP
      ? getTranslationServer('vacation.oss.components.searchfilters.labels.price_person', translationDictionary)
      : getTranslationServer('vacation.oss.components.searchfilters.labels.total_price', translationDictionary);

  const min = dynamicPrice?.from ?? 0;
  const max = dynamicPrice?.to || maxPrice; // || intentional: 0 is not a valid max
  const priceRangeFilter: Filter = {
    name: FILTER_NAME.PRICE_RANGE,
    type: FILTER_TYPE.RANGE,
    data: {
      label: priceRangeLabel,
      default: {
        min,
        max,
      },
      allowed: {
        min,
        max,
      },
      value: priceValue,
      maxValue: getMaxPrice(lang, currency),
      ...dynamicValues,
    } as FilterDataRange,
  };

  return [...[priceRangeFilter]].filter((f) => !!f);
};
